import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { Helmet } from 'react-helmet';
import { useApolloClient } from '@apollo/client';
import { Grid, CircularProgress, IconButton, Button } from '@mui/material';

import { useQuery, useMutation, useLazyQuery } from '@apollo/client';
import i18next from 'i18next';
import SaveIcon from '@mui/icons-material/Save';
import DeleteIcon from '@mui/icons-material/Delete';
import LockResetIcon from '@mui/icons-material/LockReset';
import KeyIcon from '@mui/icons-material/Key';
import LoginIcon from '@mui/icons-material/Login';

import { useForm, useFieldArray } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import yup from 'validation';

import { dispatchException, dispatchMessage } from 'helper/snackbar';

import { FormInputText } from 'components/form/FormInputText';
import { FormInputCheckbox } from 'components/form/FormInputCheckbox';
import { SpaceSelectionInput } from 'components/security/SpaceSelectionInput';
import { PaginatedSpaceSelect } from 'components/security/PaginatedSpaceSelect';
import { UnsavedChangesPrompt } from 'components/form/UnsavedChangesPrompt';
import ConfirmationButton from 'components/dialogs/ConfirmationButton';
import PasswordResetDialog from 'components/dialogs/PasswordResetDialog';
import CustomTabs from 'components/Tabs';
import SimpleTable from 'components/table/SimpleTable';

import {
  USER_VIEW_QUERY,
  UPDATE_USER_MUTATION,
  CREATE_USER_MUTATION,
  REFETCH_USERS_QUERIES,
  EVICT_USERS_QUERIES,
  RESET_USERPASSWORD_MUTATION,
  SET_USERPASSWORD_MUTATION,
  SUDO_MUTATION,
} from '../gql';
import { ME_QUERY } from 'layout/login';

import { User } from '__generated__/graphql';
import { userSelector, user2IUser, canEditAdminRecord, initialSpaceId } from 'helper/security';
import { setUser } from 'store/userSlice';
import { resetFilter, setBookingMode } from 'store/filterSlice';
import { resetReportFilters } from 'store/reportFiltersSlice';
import { resetTaskSort } from 'store/taskSortSlice';
import { filterSelector } from 'helper/filter';
import { EBookingMode } from 'types';
import { RedirectError } from 'pages/error';
import { formatDocumentTitle } from 'helper/usedocumenttitle';

interface UserProps {
  id: number;
}
interface UserCreateProps { }
interface UserFormProps {
  data: User;
}

const membershipsSchema = yup.object().shape({
  spaceId: yup.number().nullable().label('field-space'),
  spaceName: yup.string().nullable(),
  isAdmin: yup.boolean().required(),
});

const validationSchema = yup.object().shape({
  isCreate: yup.boolean().required(),
  username: yup.string().required().label('user-username'),
  email: yup.string().required().label('user-email'),
  name: yup.string().required().label('user-name'),
  isAdmin: yup.boolean(),
  isLocked: yup.boolean(),
  spaceId: yup.number().nullable().label('field-space'),
  memberships: yup.array().required().of(membershipsSchema),
});

function UserForm(props: UserFormProps) {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const client = useApolloClient();

  const user = userSelector()!;
  const canEdit = props.data.id < 0 || canEditAdminRecord(user, props.data) || props.data.id === user.id;
  const canEditAdmin = canEditAdminRecord(user, props.data);

  const [updateMutateFunction] = useMutation(UPDATE_USER_MUTATION);
  const [createMutateFunction] = useMutation(CREATE_USER_MUTATION);
  const [resetMutateFunction, { loading: resetLoading }] = useMutation(RESET_USERPASSWORD_MUTATION);
  const [setPasswordMutateFunction, { loading: setPasswordLoading }] = useMutation(SET_USERPASSWORD_MUTATION);
  const [sudoMutation] = useMutation(SUDO_MUTATION);
  const [meQuery] = useLazyQuery(ME_QUERY);

  const [createdId, setCreatedId] = useState(0);
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);

  useEffect(() => {
    if (createdId > 0) navigate(`/settings/users/${createdId}`);
  }, [createdId]);

  type UserFormType = yup.InferType<typeof validationSchema>;

  const toFormSchema = (obj: User): UserFormType => ({
    ...obj,
    isCreate: props.data.id > 0 ? false : true,
    isAdmin: !!obj.isAdmin,
    isLocked: !!obj.isLocked,
    spaceId: obj.space ? obj.space.id : 0,
    memberships: obj.memberships
      .filter(m => obj.space && obj.space.id !== m.space?.id)
      .map(m => ({
        spaceId: m.space?.id,
        spaceName: m.space?.name || null,
        isAdmin: m.isAdmin,
      })),
  });

  const {
    handleSubmit,
    control,
    trigger,
    reset,
    getValues,
    watch,
    formState: { errors: validationErrors, isDirty, isValidating, isSubmitting },
  } = useForm({
    mode: 'onChange',
    resolver: yupResolver(validationSchema) as any,
    context: { client },
    defaultValues: toFormSchema((props.data || {}) as User),
  });
  const formValues = getValues();

  const {
    fields: membershipsField,
    append: membershipsAppend,
    remove: membershipsRemove,
  } = useFieldArray({
    control,
    name: 'memberships',
  });

  const onSubmit = async (values: UserFormType) => {
    try {
      if (props.data.id > 0) {
        const res = await updateMutateFunction({
          variables: {
            id: props.data.id,
            data: {
              name: values.name,
              email: values.email,
              isAdmin: values.isAdmin,
              isLocked: values.isLocked,
              memberships: values.spaceId
                ? values.memberships.map(m => ({
                  spaceId: m.spaceId,
                  isAdmin: m.isAdmin,
                }))
                : [],
            },
          },
          update: cache => EVICT_USERS_QUERIES(cache),
          awaitRefetchQueries: true,
          refetchQueries: REFETCH_USERS_QUERIES(props.data.id),
        });
        reset(toFormSchema((res.data!.updateUser || {}) as User));
        dispatchMessage(dispatch, i18next.t('user-updated'));
      } else {
        const res = await createMutateFunction({
          variables: {
            spaceId: values.spaceId || null,
            data: {
              name: values.name,
              username: values.username,
              email: values.email,
              isAdmin: values.isAdmin,
              isLocked: values.isLocked,
              memberships: values.spaceId
                ? values.memberships.map(m => ({
                  spaceId: m.spaceId,
                  isAdmin: m.isAdmin,
                }))
                : [],
            },
          },
          update: cache => EVICT_USERS_QUERIES(cache),
          awaitRefetchQueries: true,
          refetchQueries: REFETCH_USERS_QUERIES(),
        });
        reset(toFormSchema((res.data!.createUser || {}) as User));
        setCreatedId(res.data!.createUser.id);
        dispatchMessage(dispatch, i18next.t('user-created'));
      }
    } catch (err) {
      dispatchException(dispatch, err);
    }
  };

  return (
    <>
      <Helmet>
        <title>{formatDocumentTitle([i18next.t('users-list-page-title'), props.data])}</title>
      </Helmet>
      <Grid container spacing={3}>
        <UnsavedChangesPrompt isDirty={isDirty} />
        <Grid item xs={12} sm={4}>
          <FormInputText name="name" control={control} label={i18next.t('user-name')} required disabled={!canEdit} />
        </Grid>
        <Grid item xs={12} sm={4}>
          <FormInputText name="username" control={control} label={i18next.t('user-username')} disabled={props.data.id > 0} required />
        </Grid>
        <Grid item xs={12} sm={4}>
          <FormInputText name="email" control={control} label={i18next.t('user-email')} required disabled={!canEdit} />
        </Grid>
        <Grid item xs={12} sm={4}>
          <FormInputCheckbox name="isAdmin" control={control} label={i18next.t('user-isadmin')} disabled={!canEdit || !canEditAdmin} />
        </Grid>
        <Grid item xs={12} sm={4}>
          <FormInputCheckbox name="isLocked" control={control} label={i18next.t('user-islocked')} disabled={!canEdit} />
        </Grid>
        <Grid item xs={12} sm={4}>
          <SpaceSelectionInput
            allowEmptyForRoot
            emptyLabel="[seminargo]"
            checkAdmin
            name="spaceId"
            control={control}
            disabled={!canEdit || props.data.id > 0 || user.isSingleAdminSpace}
            required
          />
        </Grid>
        {!user.isSingleSpace && (canEdit || membershipsField.length > 0) && (
          <Grid item xs={12}>
            <CustomTabs
              headers={[i18next.t('user-memberships-tab')]}
              tabs={[
                <>
                  {membershipsField.length > 0 && (
                    <SimpleTable
                      headers={[i18next.t('user-membership-space'), i18next.t('user-membership-isadmin'), '']}
                      rows={membershipsField.map((field, index) => [
                        (field as any).spaceName || user.spaces.find(s => s.id === field.spaceId)?.name,
                        <FormInputCheckbox
                          key={`${field.id}.isAdmin`}
                          name={`memberships.${index}.isAdmin`}
                          control={control}
                          label={i18next.t('user-membership-isadmin')}
                          disabled={!canEdit}
                        />,
                        canEdit && (
                          <IconButton onClick={() => membershipsRemove(index)}>
                            <DeleteIcon />
                          </IconButton>
                        ),
                      ])}
                    />
                  )}
                  {canEdit && (
                    <PaginatedSpaceSelect
                      sx={{ minWidth: 250, maxWidth: 400 }}
                      onSelect={(spaceId, spaceName) => {
                        if (props.data.space && props.data.space.id === spaceId) return;
                        if (getValues('memberships').findIndex(m => m.spaceId === spaceId) >= 0) return;
                        membershipsAppend({
                          spaceId,
                          spaceName,
                          isAdmin: true,
                        });
                      }}
                      excludeSpaceIds={[
                        watch('spaceId'),
                        ...getValues('memberships').map(m => m.spaceId),
                      ]}
                    />
                  )}
                </>,
              ]}
            />
          </Grid>
        )}
        <Grid item xs={12}>
          {canEdit && (
            <ConfirmationButton
              sx={{ marginRight: 2 }}
              variant="contained"
              startIcon={isSubmitting ? <CircularProgress size={24} /> : <SaveIcon />}
              disabled={(props.data.id > 0 && !isDirty) || isSubmitting || isValidating}
              confirmationQuestion={formValues.isLocked ? i18next.t('user-confirm-islocked') : null}
              confirmationTitle={formValues.isLocked ? i18next.t('user-confirm-islocked-title') : null}
              onConfirm={async () => {
                const valid = await trigger();
                if (valid) {
                  handleSubmit(onSubmit)();
                }
              }}
            >
              {i18next.t(props.data.id > 0 ? 'user-update' : 'user-create')}
            </ConfirmationButton>
          )}
          {props.data.id > 0 && canEdit && user.isAdmin && (
            <Button
              sx={{ marginRight: 2 }}
              variant="contained"
              color="secondary"
              startIcon={resetLoading ? <CircularProgress size={24} /> : <LockResetIcon />}
              disabled={resetLoading}
              onClick={async () => {
                try {
                  await resetMutateFunction({
                    variables: { id: props.data.id }
                  });
                  dispatchMessage(dispatch, i18next.t('user-password-reset-email-sent'));
                } catch (err) {
                  dispatchException(dispatch, err);
                }
              }}
            >
              {i18next.t('user-reset')}
            </Button>
          )}
          {props.data.id > 0 && canEdit && !user.isAdmin && (
            <Button sx={{ marginRight: 2 }} variant="contained" color="secondary" startIcon={<LockResetIcon />} disabled={true}>
              {i18next.t('user-reset')}
            </Button>
          )}
          {props.data.id > 0 && user.isRoot && (
            <Button
              sx={{ marginRight: 2 }}
              variant="contained"
              color="secondary"
              startIcon={setPasswordLoading ? <CircularProgress size={24} /> : <KeyIcon />}
              disabled={setPasswordLoading}
              onClick={() => setPasswordDialogOpen(true)}
            >
              {i18next.t('user-set-password')}
            </Button>
          )}
          {props.data.id > 0 && user.isRoot && (
            <ConfirmationButton
              sx={{ marginRight: 2 }}
              variant="contained"
              color="secondary"
              startIcon={<LoginIcon />}
              confirmationQuestion={i18next.t('user-confirm-sudo')}
              confirmationTitle={i18next.t('user-confirm-sudo-title')}
              onConfirm={async () => {
                try {
                  const res = await sudoMutation({
                    variables: { id: props.data.id },
                  });
                  const meRes = await meQuery({ fetchPolicy: 'network-only' });

                  if (res && res.data && meRes && meRes.data) {
                    if (res.data.sudo.success && res.data.sudo.user) {
                      const user = res.data.sudo.user;
                      dispatch(setUser(user2IUser(user, meRes.data.listSpaces, meRes.data.listHotels.hotels)));
                      dispatch(resetFilter());
                      dispatch(resetReportFilters());
                      dispatch(resetTaskSort());
                      dispatch(setBookingMode(EBookingMode.EPILO));
                      await client.clearStore();
                      window.location.href = '/';
                    } else {
                      dispatchException(dispatch, `${i18next.t('user-sudo-failed')}`);
                    }
                  }
                } catch (err) {
                  dispatchException(dispatch, err);
                }
              }}
            >
              {i18next.t('user-sudo')}
            </ConfirmationButton>
          )}
        </Grid>
      </Grid>
      <PasswordResetDialog
        open={passwordDialogOpen}
        setOpen={setPasswordDialogOpen}
        title={i18next.t('user-set-password-title')}
        loading={setPasswordLoading}
        onConfirm={async (password) => {
          try {
            await setPasswordMutateFunction({
              variables: { id: props.data.id, newPassword: password },
            });
            setPasswordDialogOpen(false);
            dispatchMessage(dispatch, i18next.t('user-password-set-success'));
          } catch (err) {
            dispatchException(dispatch, err);
          }
        }}
      />
    </>
  );
}

/**
 * User detail page wrapper that loads user data.
 * Renders user form for editing user information, roles, and space assignments.
 */
export default function UserUpdate(props: UserProps) {
  const userQuery = useQuery(USER_VIEW_QUERY, {
    variables: { id: props.id },
  });

  const loading = userQuery.loading;
  const error = userQuery.error;

  if (loading) return <CircularProgress />;
  else if (!loading && error) return <RedirectError err={error} />;
  else return <UserForm data={userQuery.data!.viewUser as User} />;
}

/**
 * User creation page for new users.
 */
export function UserCreate(props: UserCreateProps) {
  const filter = filterSelector();
  const user = userSelector()!;

  return (
    <UserForm
      data={{
        id: -1,
        username: '',
        name: '',
        email: '',
        hasLister: false,
        hasWidget: false,
        isAdmin: false,
        isLocked: false,
        createdAt: null,
        updatedAt: null,
        memberships: [],
        space: { id: initialSpaceId(user, filter) } as any,
      }}
    />
  );
}
